import React, { useState } from "react";
import Api from "../../api";
import { showNotification } from "../../utils";
import { parseApiDateTime } from "../../utils/datetimeFormat";
import "react-notifications-component/dist/theme.css";
import "./modificar_clases.css";

// Set to false to disable capacity enforcement if the client requests it
const ENFORCE_CAPACITY_CHECK = true;

// Set to true to prevent swaps between classes with overlapping schedules
// Currently false: admins may swap between same-time events (e.g. same-slot workshops)
const ENFORCE_SCHEDULE_CHECK = false;

const ModificarClases = () => {
  // Input states
  const [oldClassId, setOldClassId] = useState("");
  const [newClassId, setNewClassId] = useState("");

  // Class data states
  const [oldClassData, setOldClassData] = useState(null);
  const [newClassData, setNewClassData] = useState(null);

  // Student lists (local, for visual manipulation)
  const [oldStudents, setOldStudents] = useState([]);
  const [newStudents, setNewStudents] = useState([]);

  // Selected students (multi-select)
  const [selectedStudents, setSelectedStudents] = useState([]);

  // Transferred students tracking (for submit) - supports up to 12
  const [transferredStudents, setTransferredStudents] = useState([]);

  // Loading states
  const [loadingOld, setLoadingOld] = useState(false);
  const [loadingNew, setLoadingNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Validation message
  const [validationMsg, setValidationMsg] = useState(null);

  // --- Fetch class data ---
  const fetchClassData = async (classId, side) => {
    if (!classId || classId.trim() === "") {
      showNotification("Error", "Ingresa un ID de clase", "warning");
      return;
    }

    const setLoading = side === "old" ? setLoadingOld : setLoadingNew;
    const setClassData = side === "old" ? setOldClassData : setNewClassData;
    const setStudents = side === "old" ? setOldStudents : setNewStudents;

    setLoading(true);
    setValidationMsg(null);

    try {
      // Fetch class info
      const classInfo = await Api.Class.GetById(classId.trim());
      if (!classInfo) {
        showNotification("Error", `Clase ${classId} no encontrada`, "warning");
        setLoading(false);
        return;
      }
      // Inject the ID since the endpoint doesn't return it
      setClassData({ ...classInfo, id: classId.trim() });

      // Fetch student list
      const students = await Api.Class.GetAssistance(classId.trim());
      setStudents(students || []);

      showNotification("Éxito", `Clase ${classId} cargada correctamente`, "success");
    } catch (error) {
      showNotification("Error", error.message || "Error al obtener la clase", "danger");
      setClassData(null);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  // --- Validation ---
  const getValidationErrors = () => {
    if (!oldClassData || !newClassData) return null;

    if (oldClassData.id === newClassData.id) {
      return "Las clases deben ser diferentes";
    }
    if (oldClassData.language !== newClassData.language) {
      return `Los idiomas no coinciden: ${oldClassData.language} ≠ ${newClassData.language}`;
    }
    if (oldClassData.type !== newClassData.type) {
      return `Los tipos no coinciden: ${oldClassData.type} ≠ ${newClassData.type}`;
    }
    if (ENFORCE_SCHEDULE_CHECK) {
      const oldStart = new Date(oldClassData.startDate).getTime();
      const oldEnd = new Date(oldClassData.endDate).getTime();
      const newStart = new Date(newClassData.startDate).getTime();
      const newEnd = new Date(newClassData.endDate).getTime();
      const overlaps = oldStart < newEnd && newStart < oldEnd;
      if (overlaps) {
        return "Los horarios de las clases se superponen";
      }
    }
    return null;
  };

  const validationError = getValidationErrors();
  const bothClassesLoaded = oldClassData && newClassData;
  const isValid = bothClassesLoaded && !validationError;

  // --- Student selection (toggle) ---
  const handleSelectStudent = (student) => {
    setSelectedStudents((prev) => {
      const isSelected = prev.some((s) => s.customer_id === student.customer_id);
      if (isSelected) {
        return prev.filter((s) => s.customer_id !== student.customer_id);
      } else {
        return [...prev, student];
      }
    });
  };

  // --- Transfer students (visual) ---
  const handleTransfer = () => {
    if (selectedStudents.length === 0) {
      showNotification("Error", "Selecciona al menos un estudiante", "warning");
      return;
    }

    // Max 12 transfers total
    const totalAfter = transferredStudents.length + selectedStudents.length;
    if (totalAfter > 12) {
      showNotification(
        "Error",
        `Máximo 12 estudiantes por operación (actuales: ${transferredStudents.length}, seleccionados: ${selectedStudents.length})`,
        "warning"
      );
      return;
    }

    // Check if any selected student is already in the new class
    const alreadyInNew = selectedStudents.filter((sel) =>
      newStudents.some((s) => s.customer_id === sel.customer_id)
    );
    if (alreadyInNew.length > 0) {
      showNotification(
        "Error",
        `${alreadyInNew.length} estudiante(s) ya se encuentra(n) en la clase destino`,
        "warning"
      );
      return;
    }

    // Check capacity
    if (ENFORCE_CAPACITY_CHECK && newClassData) {
      const countAfter = newStudents.length + selectedStudents.length;
      if (countAfter > newClassData.capacity) {
        showNotification(
          "Error",
          `La clase destino no tiene capacidad suficiente (${newStudents.length}/${newClassData.capacity}, intentando agregar ${selectedStudents.length})`,
          "warning"
        );
        return;
      }
    }

    // Visual transfer: remove from old, add to new
    const selectedIds = new Set(selectedStudents.map((s) => s.customer_id));
    setOldStudents((prev) =>
      prev.filter((s) => !selectedIds.has(s.customer_id))
    );
    setNewStudents((prev) => [
      ...prev,
      ...selectedStudents.map((s) => ({ ...s, transferred: true })),
    ]);
    setTransferredStudents((prev) => [...prev, ...selectedStudents]);
    setSelectedStudents([]);
  };

  // --- Undo last transfer (visual) ---
  const handleUndo = () => {
    if (transferredStudents.length === 0) return;

    // Undo the last transferred student
    const lastTransferred = transferredStudents[transferredStudents.length - 1];

    // Remove from new, add back to old
    setNewStudents((prev) =>
      prev.filter((s) => s.customer_id !== lastTransferred.customer_id)
    );
    setOldStudents((prev) => [...prev, lastTransferred]);
    setTransferredStudents((prev) => prev.slice(0, -1));
    setSelectedStudents([]);
  };

  // --- Reset everything ---
  const handleReset = () => {
    setOldClassId("");
    setNewClassId("");
    setOldClassData(null);
    setNewClassData(null);
    setOldStudents([]);
    setNewStudents([]);
    setSelectedStudents([]);
    setTransferredStudents([]);
    setValidationMsg(null);
  };

  // --- Submit ---
  const handleSubmit = async () => {
    if (transferredStudents.length === 0 || !oldClassData || !newClassData) {
      showNotification("Error", "No hay cambios para enviar", "warning");
      return;
    }

    setSubmitting(true);

    try {
      const payload = transferredStudents.map((student) => ({
        studentId: student.customer_id,
        oldClassId: oldClassData.id,
        newClassId: newClassData.id,
      }));

      await Api.Class.ChangeStudent(payload);

      showNotification(
        "Éxito",
        `${transferredStudents.length} estudiante(s) cambiado(s) correctamente`,
        "success"
      );

      // Re-fetch both classes to refresh data
      setTransferredStudents([]);
      setSelectedStudents([]);

      const [updatedOldStudents, updatedNewStudents] = await Promise.all([
        Api.Class.GetAssistance(oldClassData.id),
        Api.Class.GetAssistance(newClassData.id),
      ]);

      setOldStudents(updatedOldStudents || []);
      setNewStudents(updatedNewStudents || []);
    } catch (error) {
      showNotification("Error", error.message || "Error al realizar el cambio", "danger");
    } finally {
      setSubmitting(false);
    }
  };

  const formatClassDateTime = (dateStr) => parseApiDateTime(dateStr);

  // --- Render class card ---
  const renderClassCard = (classData, students, side) => {
    if (side === "old" && loadingOld) {
      return (
        <div className="mc-card">
          <div className="mc-loading">Cargando clase...</div>
        </div>
      );
    }
    if (side === "new" && loadingNew) {
      return (
        <div className="mc-card">
          <div className="mc-loading">Cargando clase...</div>
        </div>
      );
    }

    if (!classData) {
      return (
        <div className="mc-card-placeholder">
          <p>
            {side === "old"
              ? "Ingresa el ID de la clase origen"
              : "Ingresa el ID de la clase destino"}
          </p>
        </div>
      );
    }

    return (
      <div className="mc-card">
        <div className="mc-card-header">
          <h3>
            {side === "old" ? "Clase Origen" : "Clase Destino"} — ID:{" "}
            {classData.id}
          </h3>
        </div>

        <div className="mc-card-info">
          <div className="mc-card-info-item">
            <span>Fecha inicio</span>
            <span>{formatClassDateTime(classData.startDate).date}</span>
          </div>
          <div className="mc-card-info-item">
            <span>Hora inicio</span>
            <span>{formatClassDateTime(classData.startDate).time}</span>
          </div>
          <div className="mc-card-info-item">
            <span>Fecha fin</span>
            <span>{formatClassDateTime(classData.endDate).date}</span>
          </div>
          <div className="mc-card-info-item">
            <span>Hora fin</span>
            <span>{formatClassDateTime(classData.endDate).time}</span>
          </div>
          <div className="mc-card-info-item">
            <span>Ubicación</span>
            <span>{classData.locationName || "-"}</span>
          </div>
          <div className="mc-card-info-item">
            <span>Profesor</span>
            <span>{classData.teacherName || "-"}</span>
          </div>
          <div className="mc-card-info-item">
            <span>Tipo</span>
            <span>{classData.type || "-"}</span>
          </div>
          {classData.subType && (
            <div className="mc-card-info-item">
              <span>Sub-tipo</span>
              <span>{classData.subType}</span>
            </div>
          )}
          <div className="mc-card-info-item">
            <span>Idioma</span>
            <span>{classData.language || "-"}</span>
          </div>
          <div className="mc-card-info-item">
            <span>Capacidad</span>
            <span>
              {students.length} / {classData.capacity || "-"}
            </span>
          </div>
        </div>

        <div className="mc-card-students">
          <h4>Estudiantes</h4>
          {students.length > 0 ? (
            <ul className="mc-student-list">
              {students.map((student) => (
                <li
                  key={student.customer_id}
                  className={`mc-student-item${
                    side === "old" &&
                    selectedStudents.some((s) => s.customer_id === student.customer_id)
                      ? " selected"
                      : ""
                  }${student.transferred ? " transferred" : ""}`}
                  onClick={() => {
                    if (side === "old" && isValid && !student.transferred) {
                      handleSelectStudent(student);
                    }
                  }}
                  style={{
                    cursor:
                      side === "old" && isValid && !student.transferred
                        ? "pointer"
                        : "default",
                  }}
                >
                  <span>{student.full_name || "-"}</span>
                  <span className="student-id">{student.customer_id}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mc-no-students">No hay estudiantes</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="modificar-clases-container">
      {/* Search inputs */}
      <div className="mc-search-section">
        <div className="mc-search-group">
          <label>Clase Origen (ID):</label>
          <input
            type="text"
            value={oldClassId}
            onChange={(e) => setOldClassId(e.target.value)}
            placeholder="Ej: 43017"
            disabled={submitting}
          />
          <button
            className="mc-btn mc-btn-search"
            onClick={() => fetchClassData(oldClassId, "old")}
            disabled={loadingOld || submitting}
          >
            {loadingOld ? "..." : "Buscar"}
          </button>
        </div>

        <div className="mc-search-group">
          <label>Clase Destino (ID):</label>
          <input
            type="text"
            value={newClassId}
            onChange={(e) => setNewClassId(e.target.value)}
            placeholder="Ej: 43020"
            disabled={submitting}
          />
          <button
            className="mc-btn mc-btn-search"
            onClick={() => fetchClassData(newClassId, "new")}
            disabled={loadingNew || submitting}
          >
            {loadingNew ? "..." : "Buscar"}
          </button>
        </div>
      </div>

      {/* Validation message */}
      {bothClassesLoaded && validationError && (
        <div className="mc-validation-msg error">{validationError}</div>
      )}
      {bothClassesLoaded && !validationError && (
        <div className="mc-validation-msg success">
          Las clases son compatibles para el intercambio
        </div>
      )}

      {/* Cards + Arrow */}
      <div className="mc-cards-wrapper">
        {/* Old class card */}
        {renderClassCard(oldClassData, oldStudents, "old")}

        {/* Arrow column */}
        <div className="mc-arrow-column">
          <button
            className="mc-arrow-btn"
            onClick={handleTransfer}
            disabled={!isValid || selectedStudents.length === 0}
            title="Mover estudiante(s) a la clase destino"
          >
            →
          </button>
          <button
            className="mc-arrow-btn"
            onClick={handleUndo}
            disabled={transferredStudents.length === 0}
            title="Deshacer el último movimiento"
          >
            ←
          </button>
        </div>

        {/* New class card */}
        {renderClassCard(newClassData, newStudents, "new")}
      </div>

      {/* Action buttons */}
      <div className="mc-actions">
        <button
          className="mc-btn mc-btn-submit"
          onClick={handleSubmit}
          disabled={transferredStudents.length === 0 || submitting}
        >
          {submitting ? "Procesando..." : `Confirmar cambio (${transferredStudents.length})`}
        </button>
        <button
          className="mc-btn mc-btn-reset"
          onClick={handleReset}
          disabled={submitting}
        >
          Limpiar
        </button>
      </div>
    </div>
  );
};

export default ModificarClases;
